import React, { useEffect, useRef, useState } from 'react';
import './BarcodeScanner.css';

interface Props {
  onDetected: (code: string) => void;
  onClose: () => void;
}

// The native Shape Detection API (Chromium / Android WebView) isn't in
// TypeScript's lib, so we describe just the bits we use.
type DetectedBarcode = { rawValue: string };
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
type BarcodeDetectorCtor = new (opts?: { formats?: string[] }) => BarcodeDetectorLike;

// 1D retail symbologies plus QR — what a POS realistically scans.
const FORMATS = [
  'ean_13', 'ean_8', 'upc_a', 'upc_e',
  'code_128', 'code_39', 'code_93', 'codabar', 'itf', 'qr_code',
];

// On the Android device the native BarcodeDetector is used (zero dependency).
// Desktop browsers (e.g. Windows dev) usually lack it, so we lazily pull a
// pure-JS polyfill from a CDN. `new Function` keeps webpack from trying to
// bundle the remote ES module.
const loadDetector = async (): Promise<BarcodeDetectorLike | null> => {
  const Native = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
  if (Native) {
    try { return new Native({ formats: FORMATS }); } catch { return new Native(); }
  }
  try {
    const dynImport = new Function('u', 'return import(u)') as (u: string) => Promise<unknown>;
    const mod = (await dynImport(
      'https://cdn.jsdelivr.net/npm/barcode-detector@2/dist/es/pure.min.js'
    )) as { BarcodeDetector: BarcodeDetectorCtor };
    return new mod.BarcodeDetector({ formats: FORMATS });
  } catch {
    return null;
  }
};

const BarcodeScanner: React.FC<Props> = ({ onDetected, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Starting camera…');

  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;

    const stop = () => {
      stopped = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };

    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setError('Camera is not available in this browser.');
          return;
        }
        const detector = await loadDetector();
        if (!detector) {
          setError('Barcode scanning is not supported in this browser. It works on the Android device.');
          return;
        }
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
        });
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        setStatus('Point the camera at a barcode');

        const tick = async () => {
          if (stopped) return;
          try {
            const codes = await detector.detect(video);
            const value = codes[0]?.rawValue?.trim();
            if (value) { onDetected(value); return; }
          } catch {
            /* transient decode error — keep scanning */
          }
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      } catch (e) {
        const name = (e as { name?: string })?.name;
        if (name === 'NotAllowedError' || name === 'SecurityError') setError('Camera permission was denied.');
        else if (name === 'NotFoundError') setError('No camera was found on this device.');
        else setError('Could not start the camera.');
      }
    })();

    return stop;
  }, [onDetected]);

  return (
    <div className="bc-scan-overlay" onClick={onClose}>
      <div className="bc-scan-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bc-scan-head">
          <span>Scan barcode</span>
          <span className="bc-scan-x" onClick={onClose}>✕</span>
        </div>
        <div className="bc-scan-body">
          {error ? (
            <div className="bc-scan-error">{error}</div>
          ) : (
            <>
              <video ref={videoRef} className="bc-scan-video" muted playsInline />
              <div className="bc-scan-reticle" />
              <div className="bc-scan-status">{status}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner;
